import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getApiBase } from "../apiBase";
import { FaEye, FaEyeSlash, FaLock, FaCheckCircle } from "react-icons/fa";
import { motion } from "framer-motion";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const passwordRules = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const meetsAllRules = Object.values(passwordRules).every(Boolean);
  const passwordsMatch = confirmPassword && password === confirmPassword;

  const handleReset = async () => {
    setError("");

    if (!password || !confirmPassword) {
      setError("Please fill all fields");
      return;
    }

    if (!meetsAllRules) {
      setError("Password does not meet security requirements");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${getApiBase()}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || "Something went wrong");
        return;
      }

      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2E2C5A] via-[#3E2D55] to-[#5B1C32] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md p-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
            <FaCheckCircle className="text-green-400" size={32} />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Password Updated</h2>
          <p className="text-gray-300 text-sm">Redirecting to login...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E2C5A] via-[#3E2D55] to-[#5B1C32] flex items-center justify-center p-4">
      {/* Subtle animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#FFB347]/20 rounded-full"
            animate={{
              x: [0, Math.random() * 100 - 50, 0],
              y: [0, Math.random() * 100 - 50, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md p-8 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl relative z-10"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 mx-auto mb-4 bg-[#FFB347]/20 rounded-xl flex items-center justify-center">
            <FaLock className="text-[#FFB347]" size={24} />
          </div>
          <h2 className="text-xl font-semibold text-white">Create New Password</h2>
          <p className="text-gray-300 text-sm mt-1">
            Your new password must be different from previous passwords
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-red-500/20 border border-red-400/30 rounded-lg text-red-200 text-sm text-center"
          >
            {error}
          </motion.div>
        )}

        {/* Form */}
        <div className="space-y-4">
          {/* New Password Field */}
          <div className="space-y-1.5">
            <label className="text-xs text-gray-300 font-medium ml-1">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white placeholder-gray-400 focus:border-[#FFB347] focus:ring-1 focus:ring-[#FFB347] outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          {password && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="p-3 bg-white/5 rounded-xl space-y-1.5"
            >
              <p className="text-xs text-gray-400 font-medium mb-2">Password must contain:</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                <RuleItem met={passwordRules.length} text="8+ characters" />
                <RuleItem met={passwordRules.upper} text="Uppercase letter" />
                <RuleItem met={passwordRules.lower} text="Lowercase letter" />
                <RuleItem met={passwordRules.number} text="Number" />
                <RuleItem met={passwordRules.special} text="Special character" />
              </div>
            </motion.div>
          )}

          {/* Confirm Password Field */}
          <div className="space-y-1.5">
            <label className="text-xs text-gray-300 font-medium ml-1">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError("");
                }}
                className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-sm text-white placeholder-gray-400 focus:outline-none transition-all ${
                  confirmPassword && passwordsMatch
                    ? "border-green-400/50 focus:border-green-400 focus:ring-1 focus:ring-green-400"
                    : confirmPassword && !passwordsMatch
                    ? "border-red-400/50 focus:border-red-400 focus:ring-1 focus:ring-red-400"
                    : "border-white/20 focus:border-[#FFB347] focus:ring-1 focus:ring-[#FFB347]"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                tabIndex={-1}
              >
                {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            {confirmPassword && passwordsMatch && (
              <p className="text-xs text-green-400 ml-1 flex items-center gap-1">
                <FaCheckCircle size={10} /> Passwords match
              </p>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: meetsAllRules && passwordsMatch ? 1.02 : 1 }}
            whileTap={{ scale: meetsAllRules && passwordsMatch ? 0.98 : 1 }}
            onClick={handleReset}
            disabled={loading || !meetsAllRules || !passwordsMatch}
            className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
              meetsAllRules && passwordsMatch && !loading
                ? "bg-gradient-to-r from-[#FFB347] to-[#FFCC70] text-[#2E2C5A] hover:opacity-95"
                : "bg-white/10 text-gray-400 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-[#2E2C5A]/30 border-t-[#2E2C5A] rounded-full animate-spin" />
                Updating...
              </span>
            ) : (
              "Reset Password"
            )}
          </motion.button>
        </div>

        {/* Footer */}
        <p className="text-xs text-center text-gray-400 mt-6">
          Remember your password?{" "}
          <button
            onClick={() => navigate("/login")}
            className="text-[#FFB347] hover:underline focus:outline-none"
          >
            Back to Sign In
          </button>
        </p>
      </motion.div>
    </div>
  );
}

// Helper component for password rule items
function RuleItem({ met, text }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`w-3 h-3 rounded-full flex items-center justify-center ${
          met ? "bg-green-400" : "bg-gray-500/50"
        }`}
      >
        {met && <FaCheckCircle size={8} className="text-[#2E2C5A]" />}
      </span>
      <span className={met ? "text-green-300" : "text-gray-500"}>{text}</span>
    </div>
  );
}